import React from 'react';
import {
  MonthlyPlan,
  MonthlyPlanFixedExpense
} from '../../../../apiServices/types';
import {
  formatCurrency,
  formatDateKey,
  formatWeekRange,
  getCurrencyName,
  MonthlyPlanSummary,
  parseCurrency,
  PlanPeriod,
  sortFixedExpensesForDashboard
} from '../../../../utils';
import WeeklyBreakdown from './WeeklyBreakdown';
import { FIXED_STATUS_LABELS, getPercent, getProgressLevel, getRecurrenceLabel } from './planDisplay';

interface MonthlyPlanDashboardProps {
  period: PlanPeriod;
  plan: MonthlyPlan;
  summary: MonthlyPlanSummary;
  payingItemId: string | null;
  onPay: (item: MonthlyPlanFixedExpense) => void;
}

interface ProgressRowProps {
  name: string;
  meta?: React.ReactNode;
  actual: number;
  planned: number;
  level?: 'good' | 'warning' | 'over';
  aside?: React.ReactNode;
  actions?: React.ReactNode;
  isCompleted?: boolean;
}

const ProgressRow: React.FC<ProgressRowProps> = ({ name, meta, actual, planned, level, aside, actions, isCompleted = false }) => {
  const percent = getPercent(actual, planned);

  return (
    <div className={`budget-category-card monthly-plan-item ${isCompleted ? 'monthly-plan-item-completed' : ''}`}>
      <div className="budget-category-header">
        <div className="monthly-plan-item-info">
          <span className="budget-category-name">{name}</span>
          {meta && <span className="monthly-plan-item-meta">{meta}</span>}
        </div>
        <div className="monthly-plan-item-side">
          {aside}
          <div className="budget-category-amounts">
            <span className="budget-category-actual">{formatCurrency(actual)}</span>
            <span className="budget-category-separator">/</span>
            <span className="budget-category-planned">{formatCurrency(planned)}</span>
          </div>
          {actions}
        </div>
      </div>
      <div className="budget-category-progress">
        <div
          className={`budget-category-progress-fill category-progress-${level ?? getProgressLevel(percent)}`}
          style={{ width: `${Math.max(0, Math.min(percent, 100))}%` }}
        ></div>
        {percent > 100 && (level ?? getProgressLevel(percent)) !== 'good' && (
          <div className="budget-category-progress-overfill" style={{ width: `${Math.min(percent - 100, 100)}%` }}></div>
        )}
      </div>
    </div>
  );
};

const MonthlyPlanDashboard: React.FC<MonthlyPlanDashboardProps> = ({ period, plan, summary, payingItemId, onPay }) => {
  const netSpent = summary.variableSpent - summary.returned;
  const freeUsage = getPercent(netSpent, summary.freeAmount);
  const freeLevel = summary.freeLeft < 0 ? 'over' : getProgressLevel(freeUsage);
  const { currentWeek } = summary;
  const isFinished = summary.weeks.every(week => week.status === 'past');
  const isUpcoming = summary.weeks.every(week => week.status === 'future');
  const monthStatus = isFinished ? 'completed' : isUpcoming ? 'upcoming' : 'active';
  const planItemsById = new Map(plan.fixedExpenses.map(item => [item.id, item]));
  const savingsById = new Map(summary.savingsGoals.map(goal => [goal.key, goal]));
  const paidCount = summary.fixedExpenses.filter(item => item.status === 'paid').length;
  const currentWeekNet = currentWeek ? currentWeek.spent - currentWeek.returned : 0;
  const currentWeekUsage = currentWeek ? getPercent(currentWeekNet, currentWeek.budget) : 0;

  return (
    <div className="monthly-plan-dashboard">
      {/* Огляд */}
      <div className={`budget-card budget-card-${monthStatus === 'completed' ? 'completed' : 'active'}`}>
        <div className="budget-card-header">
          <div className="budget-card-title-section no-collapse">
            <div className="budget-status-indicator-wrapper">
              <div className={`budget-status-indicator budget-status-indicator-${monthStatus}`}></div>
            </div>
            <div className="budget-title-content">
              <h3 className="budget-card-title">Вільні кошти</h3>
              <div className="budget-card-meta">
                <span className="budget-date-range">
                  Дохід {formatCurrency(summary.totalIncome)} − сталі {formatCurrency(summary.totalFixed)} − заощадження {formatCurrency(summary.totalSavings)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="budget-card-body">
          <div className="budget-stats-grid">
            <div className="budget-stat">
              <div className="budget-stat-label">Заплановано</div>
              <div className="budget-stat-value budget-stat-total">{formatCurrency(summary.freeAmount)}</div>
            </div>
            <div className="budget-stat">
              <div className="budget-stat-label">Витрачено</div>
              <div className="budget-stat-value budget-stat-spent">{formatCurrency(netSpent)}</div>
            </div>
            <div className={`budget-stat budget-stat-remaining ${summary.freeLeft < 0 ? 'budget-stat-over' : ''} budget-stat-progress-${freeLevel}`}>
              <div className="budget-stat-label">Залишок</div>
              <div className="budget-stat-value">{formatCurrency(summary.freeLeft)}</div>
            </div>
          </div>

          <div className="budget-progress-wrapper">
            <div className="budget-progress-header">
              <span className="budget-progress-label">Використано вільних коштів</span>
              <span className="budget-progress-percentage">{freeUsage.toFixed(1)}%</span>
            </div>
            <div className="budget-progress-bar">
              <div
                className={`budget-progress-fill budget-progress-${freeLevel}`}
                style={{ width: `${Math.max(0, Math.min(freeUsage, 100))}%` }}
              ></div>
              {freeUsage > 100 && (
                <div className="budget-progress-overfill" style={{ width: `${Math.min(freeUsage - 100, 100)}%` }}></div>
              )}
            </div>
          </div>
        </div>

        <div className="budget-categories-section">
          <div className="budget-categories-header">
            <span className="budget-categories-title">Виконання плану</span>
          </div>
          <div className="budget-categories-list">
            <ProgressRow
              name="Дохід отримано"
              meta={plan.incomes.map(income => income.name).join(', ')}
              actual={summary.actualIncome}
              planned={summary.totalIncome}
              level="good"
            />
            <ProgressRow
              name="Сталі витрати сплачено"
              meta={`${paidCount} з ${summary.fixedExpenses.length} платежів`}
              actual={summary.fixedActual}
              planned={summary.totalFixed}
            />
            <ProgressRow
              name="Відкладено в заощадження"
              meta={summary.unplannedSavings > 0 ? `+ ${formatCurrency(summary.unplannedSavings)} поза планом` : undefined}
              actual={summary.savingsActual}
              planned={summary.totalSavings}
              level="good"
            />
          </div>
        </div>
      </div>

      {/* Поточний тиждень */}
      {currentWeek && (
        <div className="budget-card monthly-plan-current-week">
          <div className="budget-card-header">
            <div className="budget-card-title-section no-collapse">
              <div className="budget-status-indicator-wrapper">
                <div className="budget-status-indicator budget-status-indicator-active"></div>
              </div>
              <div className="budget-title-content">
                <h3 className="budget-card-title">Цей тиждень</h3>
                <div className="budget-card-meta">
                  <span className="budget-date-range">{formatWeekRange(currentWeek, period)}</span>
                  <span className="budget-type-badge">Залишилось днів: {currentWeek.daysLeft}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="budget-card-body">
            <div className="budget-stats-grid">
              <div className="budget-stat">
                <div className="budget-stat-label">Бюджет</div>
                <div className="budget-stat-value budget-stat-total">{formatCurrency(currentWeek.budget)}</div>
              </div>
              <div className="budget-stat">
                <div className="budget-stat-label">Витрачено</div>
                <div className="budget-stat-value budget-stat-spent">{formatCurrency(currentWeekNet)}</div>
              </div>
              <div className={`budget-stat budget-stat-remaining ${currentWeek.left < 0 ? 'budget-stat-over budget-stat-progress-over' : `budget-stat-progress-${getProgressLevel(currentWeekUsage)}`}`}>
                <div className="budget-stat-label">Доступно</div>
                <div className="budget-stat-value">{formatCurrency(currentWeek.left)}</div>
              </div>
            </div>
            <p className="monthly-plan-hint">
              {currentWeek.left > 0 && currentWeek.daysLeft > 0
                ? `≈ ${formatCurrency(currentWeek.left / currentWeek.daysLeft)} на день до кінця тижня`
                : currentWeek.left < 0
                  ? `Перевитрату ${formatCurrency(Math.abs(currentWeek.left))} буде розподілено на наступні тижні`
                  : 'Бюджет тижня використано'}
              {Math.abs(currentWeek.carryOver) >= 0.01 && (
                <> · {currentWeek.carryOver > 0 ? 'з попередніх тижнів перенесено +' : 'з попередніх тижнів перевитрата −'}{formatCurrency(Math.abs(currentWeek.carryOver))}</>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Тижні */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">Розподіл по тижнях</h3>
            <p className="monthly-plan-section-subtitle">
              Невитрачені кошти переходять на наступні тижні, перевитрата — зменшує їх
            </p>
          </div>
        </div>
        <div className="monthly-plan-section-body">
          <WeeklyBreakdown weeks={summary.weeks} period={period} />
        </div>
      </div>

      {/* Сталі витрати */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">Сталі витрати</h3>
            <p className="monthly-plan-section-subtitle">
              Платежі з шаблонів зараховуються автоматично, власні витрати — за категорією
            </p>
          </div>
          <span className="monthly-plan-section-total">{formatCurrency(summary.totalFixed)}</span>
        </div>
        <div className="monthly-plan-section-body">
          {plan.fixedExpenses.length === 0 ? (
            <p className="monthly-plan-empty-text">Сталих витрат у плані немає</p>
          ) : (
            <div className="budget-categories-list">
              {sortFixedExpensesForDashboard(summary.fixedExpenses).map(progress => {
                const item = planItemsById.get(progress.key);
                if (!item) return null;

                const status = progress.isTracked ? progress.status : 'untracked';
                const statusLabel = progress.isTracked ? FIXED_STATUS_LABELS[progress.status] : 'Без обліку';
                const canPay = !!item.recurringExpenseId && progress.status !== 'paid';
                const lastPayment = progress.payments[progress.payments.length - 1];
                const meta = [
                  item.recurringExpenseId
                    ? getRecurrenceLabel(item.recurrenceType, item.dayOfMonth, item.paymentsCount)
                    : item.categoryName ? `категорія «${item.categoryName}»` : 'без категорії',
                  progress.expectedPayments && progress.expectedPayments > 1
                    ? `платежів ${progress.paymentsMade ?? 0} з ${progress.expectedPayments}`
                    : null,
                  progress.dueDateKey && progress.status !== 'paid' && item.dayOfMonth
                    ? `до ${formatDateKey(progress.dueDateKey)}`
                    : null,
                  lastPayment && item.recurringExpenseId ? `оплачено ${formatDateKey(lastPayment.date)}` : null,
                  progress.overAmount > 0 ? `понад план ${formatCurrency(progress.overAmount)}` : null
                ].filter(Boolean).join(' · ');

                return (
                  <ProgressRow
                    key={item.id}
                    name={item.name}
                    meta={meta}
                    actual={progress.actual}
                    planned={progress.planned}
                    level={progress.overAmount > 0 ? 'warning' : 'good'}
                    isCompleted={progress.status === 'paid'}
                    aside={<span className={`monthly-plan-status monthly-plan-status-${status}`}>{statusLabel}</span>}
                    actions={canPay && (
                      <button
                        type="button"
                        className="recurring-action-icon recurring-action-icon-primary"
                        onClick={() => onPay(item)}
                        disabled={payingItemId !== null}
                        title={payingItemId === item.id ? 'Створення...' : 'Сплатити — створити чек з шаблону'}
                      >
                        {payingItemId === item.id ? (
                          <div className="spinner-small"></div>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2v20M2 12h20"></path>
                          </svg>
                        )}
                      </button>
                    )}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Заощадження */}
      <div className="budget-card monthly-plan-section">
        <div className="monthly-plan-section-header">
          <div>
            <h3 className="budget-card-title">Заощадження</h3>
            <p className="monthly-plan-section-subtitle">Перекази на рахунки заощаджень цього місяця</p>
          </div>
          <span className="monthly-plan-section-total">{formatCurrency(summary.totalSavings)}</span>
        </div>
        <div className="monthly-plan-section-body">
          {plan.savingsGoals.length === 0 ? (
            <p className="monthly-plan-empty-text">Цілей заощаджень у плані немає</p>
          ) : (
            <div className="budget-categories-list">
              {plan.savingsGoals.map(goal => {
                const progress = savingsById.get(goal.id);
                if (!progress) return null;

                return (
                  <ProgressRow
                    key={goal.id}
                    name={goal.accountName}
                    meta={progress.remaining > 0
                      ? `залишилось відкласти ${formatCurrency(progress.remaining)}`
                      : `ціль виконано · ${getCurrencyName(parseCurrency(goal.currency))}`}
                    actual={progress.actual}
                    planned={progress.planned}
                    level="good"
                    aside={progress.remaining <= 0 ? <span className="monthly-plan-status monthly-plan-status-paid">Виконано</span> : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyPlanDashboard;
